import { useEmail } from '../hooks/useEmail';
import { Card, CardContent, Typography, Box, useMediaQuery, IconButton, Tooltip, Snackbar } from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';

// Custom styled components
const MailboxCard = styled(Card)(() => ({
  marginBottom: '1rem',
  borderRadius: '12px',
  overflow: 'hidden',
  background: 'rgba(30, 30, 45, 0.5)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease',
  marginTop: '20px',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)'
  }
}));

const MailboxHeader = styled(Box)(({ theme }) => ({
  padding: '1rem 1.5rem',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const GradientText = styled(Typography)(() => ({
  background: 'linear-gradient(45deg, #646cff, #9e9eff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 600,
  fontSize: '1.3rem',
  margin: '0',
}));

function CurrentMailbox() {
  const { currentInbox, createNewMailbox } = useEmail();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [copied, setCopied] = useState(false);
  
  const handleGetNewEmail = async () => {
    try {
      await createNewMailbox();
    } catch (error) {
      console.error("Failed to create new mailbox:", error);
    }
  };
  
  const handleCopyEmail = () => {
    if (currentInbox) {
      navigator.clipboard.writeText(currentInbox.email)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error("Failed to copy email address:", err);
        });
    }
  };
  
  if (!currentInbox) {
    return (
      <MailboxCard>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No mailbox selected
          </Typography>
        </CardContent>
      </MailboxCard>
    );
  }
  
  return (
    <MailboxCard>
      <MailboxHeader>
        <Typography 
          variant="h6" 
          color="text.primary"
          sx={{ fontWeight: 500 }}
        >
          Current Inbox
        </Typography>
      </MailboxHeader>
      <CardContent sx={{ p: isMobile ? 1 : 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <GradientText variant={isMobile ? "body1" : "h6"}>
            {currentInbox.email}
          </GradientText>
          <Tooltip title="Copy email address">
            <IconButton 
              onClick={handleCopyEmail} 
              size="small" 
              color="primary"
              sx={{ 
                backgroundColor: 'rgba(100, 108, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(100, 108, 255, 0.15)'
                }
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Get new email address">
            <IconButton 
              onClick={handleGetNewEmail} 
              size="small" 
              color="primary"
              sx={{ 
                backgroundColor: 'rgba(100, 108, 255, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(100, 108, 255, 0.15)'
                }
              }}
            >
              <ShuffleIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        message="Email address copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </MailboxCard>
  );
}

export default CurrentMailbox;